import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useTrackingStore from '../tracking/useTrackingStore';
import { operationalAssets } from '../tracking/deploymentUsageModel';
import { getMemberAccessToken } from '../auth/supabase';
import './admin.css';

function parseDateOnly(value) {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysUntil(value) {
  const date = parseDateOnly(value);
  if (!date) return null;
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  return Math.ceil((date.getTime() - now.getTime()) / 86400000);
}

function attentionLabel(asset) {
  const status = String(asset?.status || '').toLowerCase();
  const condition = String(asset?.condition || '').toLowerCase();
  if (status === 'damaged' || condition === 'damaged') return 'Damaged';
  if (status === 'needs_restock' || condition === 'needs_restock') return 'Needs restock';
  if (status === 'needs_inventory' || condition === 'needs_inventory') return 'Needs inventory';
  if (status === 'out_of_service' || condition === 'out_of_service') return 'Out of service';
  if (status === 'pending_return') return 'Pending return';
  return String(asset?.status || asset?.condition || 'Attention').replaceAll('_', ' ');
}

export default function AdminAttention({ profile }) {
  const { state, sync } = useTrackingStore(profile?.id);
  const [inventory, setInventory] = useState({ expirations: [] });
  const [handoffs, setHandoffs] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!profile?.is_admin) return;
    setError('');
    try {
      const token = await getMemberAccessToken();
      if (!token) throw new Error('Member sign in required.');
      const [inventoryResponse, handoffResponse] = await Promise.all([
        fetch('/api/tracking?admin=attention_inventory', {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch('/api/gear-handoff', {
          method: 'POST',
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'admin_list' }),
        }),
      ]);
      const inventoryPayload = await inventoryResponse.json().catch(() => ({}));
      const handoffPayload = await handoffResponse.json().catch(() => ({}));
      if (!inventoryResponse.ok) throw new Error(inventoryPayload?.error || 'Unable to load inventory exceptions.');
      if (!handoffResponse.ok) throw new Error(handoffPayload?.error || 'Unable to load gear handoffs.');
      setInventory(inventoryPayload || { expirations: [] });
      setHandoffs(Array.isArray(handoffPayload?.sessions) ? handoffPayload.sessions : []);
    } catch (loadError) {
      setError(loadError?.message || 'Unable to load attention items.');
    }
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const assets = useMemo(() => operationalAssets(state), [state]);
  const gearAttention = useMemo(
    () => assets.filter(asset => {
      const status = String(asset?.status || '').toLowerCase();
      const condition = String(asset?.condition || '').toLowerCase();
      return ['needs_restock', 'needs_inventory', 'damaged', 'out_of_service', 'pending_return'].includes(status)
        || ['needs_restock', 'needs_inventory', 'damaged', 'out_of_service'].includes(condition);
    }),
    [assets]
  );

  const expiring = useMemo(() => {
    return (inventory.expirations || [])
      .filter(item => item.expirationState === 'DATED' && !item.returnedToLogistics)
      .map(item => ({ ...item, days: daysUntil(item.expirationDate) }))
      .filter(item => item.days != null && item.days <= 90)
      .sort((a, b) => a.days - b.days);
  }, [inventory.expirations]);

  const expired = expiring.filter(item => item.days < 0);
  const within30 = expiring.filter(item => item.days >= 0 && item.days <= 30);
  const within60 = expiring.filter(item => item.days > 30 && item.days <= 60);
  const within90 = expiring.filter(item => item.days > 60 && item.days <= 90);
  const activeDeployments = (state.deployments || []).filter(item => item.status !== 'closed');

  if (!profile?.is_admin) return <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1></section></main></div>;

  return (
    <div className="admin-root">
      <header className="admin-topbar">
        <div><h1>Needs Attention</h1><p>Gear, handoffs, deployments, and expiring inventory that may need Program Admin follow-up.</p></div>
        <div className="admin-topbar-actions"><button type="button" onClick={load}>Refresh</button><button type="button" onClick={() => window.location.assign('/admin')}>Admin Home</button></div>
      </header>
      <main className="admin-page">
        {error && <div className="v12-error">{error}</div>}
        <div className="admin-summary-grid">
          <div className="admin-stat"><span>Active Deployments</span><strong>{activeDeployments.length}</strong></div>
          <div className="admin-stat"><span>Gear Attention</span><strong>{gearAttention.length}</strong></div>
          <div className="admin-stat"><span>Open Handoffs</span><strong>{handoffs.length}</strong></div>
          <div className="admin-stat"><span>Expired</span><strong>{expired.length}</strong></div>
          <div className="admin-stat"><span>Expiring ≤30d</span><strong>{within30.length}</strong></div>
          <div className="admin-stat"><span>Expiring 31–90d</span><strong>{within60.length + within90.length}</strong></div>
        </div>

        <section className="admin-card">
          <div className="admin-section-head"><div><h2>Gear Exceptions</h2><p>Physical assets not currently in a normal ready/available state.</p></div><button type="button" onClick={() => window.location.assign('/admin/deployments?tab=gear')}>Open Gear Status</button></div>
          {!gearAttention.length && <div className="admin-empty">No gear exceptions right now.</div>}
          {gearAttention.map(asset => (
            <div className="admin-table-row" key={asset.id}>
              <span><strong>{asset.name}</strong><small>{asset.id}{asset.assignedTo ? ` · ${asset.assignedTo}` : ''}</small></span>
              <strong>{attentionLabel(asset)}</strong>
            </div>
          ))}
        </section>

        <section className="admin-card">
          <div className="admin-section-head"><div><h2>Open Gear Handoffs</h2><p>Transfer or return-on-behalf sessions that have not reached a completed/revoked state.</p></div></div>
          {!handoffs.length && <div className="admin-empty">No open handoffs.</div>}
          {handoffs.map(item => (
            <div className="admin-table-row" key={item.id}>
              <span>
                <strong>{item.outgoing_name || 'Member'} → {item.receiving_name || 'Awaiting receiver'}</strong>
                <small>{item.incident || 'No incident'} · {item.handoff_type === 'return_on_behalf' ? 'Return for me' : 'Take over gear'} · {new Date(item.created_at).toLocaleString()}</small>
              </span>
              <strong>{item.status}</strong>
            </div>
          ))}
        </section>

        <section className="admin-card">
          <div className="admin-section-head"><div><h2>Expiration Watch</h2><p>Inventory lots already expired or reaching expiration within 90 days.</p></div><button type="button" onClick={() => window.location.assign('/inventory-admin')}>Open Inventory</button></div>
          {!expiring.length && <div className="admin-empty">No inventory expires within 90 days.</div>}
          {expiring.map(item => (
            <div className="admin-table-row" key={item.recordId}>
              <span><strong>{item.item}</strong><small>{item.setName || item.setId} · {item.component}{item.lotSerial ? ` · Lot ${item.lotSerial}` : ''}</small></span>
              <strong>{item.days < 0 ? `Expired ${Math.abs(item.days)}d ago` : item.days === 0 ? 'Expires today' : `${item.days}d`}</strong>
            </div>
          ))}
        </section>

        <div className="v12-admin-note">Tracking sync: {sync.status}{sync.error ? ` · ${sync.error}` : ''}</div>
      </main>
    </div>
  );
}
